import { Inject } from '@odinjs/odin';

import { ContextualBarAPI, FormAPI, InputAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import moment from 'moment';

import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { AdultTpnTerminateAliases, FragmentNames } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../BaseController';
import { SolutionType } from '../../../adjust-infusion-speed/adjust-infusion-speed-types';
import MedicationService from '../../../services/medication-service';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';

interface GenerateChangePrescribedSolutionResponse {
  messageDescription: string;
}

type AdultTpnTerminateRequestBody = {
  changeDate?: string,
  changeType?: number;
  dateHour?: string,
  despisedVolumeQuantity?: number,
  encounterNumber?: number,
  infusedVolumeQuantity?: number,
  infusionSpeedQuantity?: number,
  justificationDescription?: string,
  partialVolumeQuantity?: number,
  prescriptionNumber?: number,
  solutionSequenceNumber?: number,
  solutionType?: number,
  volumePhaseQuantity?: number,
}

@Controller({ component: AdultTpnTerminateAliases.ADULT_TPN_TERMINATE_LAYOUT, fragment: FragmentNames.ADULT_TPN_TERMINATE })
export default class AdultTpnTerminateAliasesController extends BaseController {

  @Inject(AdultTpnTerminateAliases.ADULT_TPN_TERMINATE_FORM)
  private interruptFormAPI!: FormAPI;

  @Inject(AdultTpnTerminateAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @Inject(AdultTpnTerminateAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(AdultTpnTerminateAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaAPI!: InputAPI;

  @Inject(AdultTpnTerminateAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @Inject(AdultTpnTerminateAliases.DATE_INPUT)
  private newDateInputApi!: InputAPI;

  @OnMount(AdultTpnTerminateAliases.CONTEXTUAL_BAR_FORM)
  handleMountContextualBarForm({ target: contextualBarApi }: EventData<ContextualBarAPI>): void {
    const materialDescription = MedicineAndSolutionsParams.params.dsMaterialId as string
    contextualBarApi.setTitle(materialDescription)
  }

  @OnMount(AdultTpnTerminateAliases.ADULT_TPN_TERMINATE_FORM)
  handleMountForm({ target: formApi }: EventData<FormAPI>): void {
    const defaultValue = 0
    formApi.setData({
      discardedVolume: defaultValue,
      infusedVolume: defaultValue,
      observation: '',
      partialVolume: defaultValue,
      stageVolume: MedicineAndSolutionsParams.params.totalVolume ?? defaultValue,
    })
  }

  @OnMount(AdultTpnTerminateAliases.DATE_INPUT)
  handleDateFieldMount({ target: inputApi }: EventData<InputAPI>): void {
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    inputApi.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnBeforeSubmit(AdultTpnTerminateAliases.ADULT_TPN_TERMINATE_FORM)
  handleBeforeSubmit(): void {
    const body = this.getRequestBody()
    this.handleTerminateEnteralNutritionRequest(body);
  }

  @OnBeforeCancel(AdultTpnTerminateAliases.ADULT_TPN_TERMINATE_FORM)
  onFormCancel(): void {
    applicationContext.popNavigationItem();
  }

  @OnChange(AdultTpnTerminateAliases.INFUSED_VOLUME_INPUT)
  handleInfusedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  @OnChange(AdultTpnTerminateAliases.DISCARDED_VOLUME_INPUT)
  handleDiscardedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  private getRequestBody(): AdultTpnTerminateRequestBody {
    const { encounterId, prescriptionNumber, startDate, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const changeTypeTerminate = 4;
    const changeDate = moment(this.newDateInputApi.getValue()).format(ISO_FULL_DATE_FORMAT)

    return {
      changeDate,
      changeType: changeTypeTerminate,
      dateHour: moment(startDate as string).format(ISO_FULL_DATE_FORMAT),
      despisedVolumeQuantity: this.discardedVolumeInputAPI.getValue() as number,
      encounterNumber: encounterId as number,
      infusedVolumeQuantity: this.infusedVolumeInputAPI.getValue() as number,
      justificationDescription: this.notesJustificationTextAreaAPI.getValue() as string,
      partialVolumeQuantity: this.partialVolumeInputAPI.getValue() as number,
      prescriptionNumber: Number(prescriptionNumber),
      solutionSequenceNumber: Number(sequenceNumber),
      solutionType: SolutionType.ADULT_PARENTERAL_NUTRITION,
      volumePhaseQuantity: this.infusedVolumeInputAPI.getValue() as number,
    }
  }

  private handleTerminateEnteralNutritionRequest(body: AdultTpnTerminateRequestBody): Promise<void> {
    this.interruptFormAPI.setLoading(true)
    this.interruptFormAPI.setEditing(false)
    return HttpService.post<GenerateChangePrescribedSolutionResponse>(Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR, {}, { body })
      .then(({ body: responseBody }) => {
        if (responseBody) {
          this.showResponseMessage(responseBody?.messageDescription);
          this.redirectToTimeline();
        }
      }).catch(error => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
      }).finally(() => {
        this.interruptFormAPI.setEditing(true)
        this.interruptFormAPI.setLoading(false)
      });
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('label.infusion-viewer.toast-infusion-terminated'), ToastLevel.SUCCESS);
  }

  private isValidChar(value: string, oldValue?: string): boolean {
    return value === '.' || value === ',' || (value === '' && oldValue !== '')
  }

  private handleVolumeInputChange({ value, oldValue }: ChangeEvent<string>, input: InputAPI): void {
    const actualValue = Number(value) || 0;
    if (this.isValidChar(value, oldValue) || isNaN(actualValue) || oldValue === value) {
      return
    }
    const doseQuantity = Number(MedicineAndSolutionsParams.params.totalVolume);
    const partialVolume = Number(this.partialVolumeInputAPI.getValue());
    const updatedVolume = this.calculateVolumeSolution(actualValue, partialVolume, Number(doseQuantity));
    input.setValue(String(updatedVolume));
  }

  private calculateVolumeSolution(infusedVolume: number, partialVolume: number, stageVolume: number): number {
    const defaultValue = 0;
    if (stageVolume > defaultValue) {
      const result = stageVolume - partialVolume - infusedVolume;
      return Math.max(defaultValue, parseFloat(result.toFixed(2)));
    }

    return defaultValue;
  }
}